import { SapNewOrder } from '@allurion/domain';
import { InfoIcon } from '@allurion/ui';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { TrackedEmptyState, TrackedPageHeader } from 'src/analytics/TrackedUI';
import { Seo } from 'src/components/Seo';
import { InlineLoader } from 'src/components/ui/InlineLoader';
import { toastSuccess } from 'src/components/ui/toasts';
import { useAppNavigate } from 'src/hooks/useAppNavigate';
import { useClinicOrders } from 'src/hooks/useClinicOrders';
import { useClinicOrderSetup } from 'src/hooks/useClinicOrderSetup';

import messages from './clinic-orders.messages';
import { OrderForm } from './OrderForm';

export function CreateOrderPage() {
  const intl = useIntl();
  const { clinicId } = useParams<{ clinicId: string }>();
  const { toClinicOrdersPage } = useAppNavigate();
  const { billingAddresses, shippingAddresses, purchasableItems, accountName, isLoading } =
    useClinicOrderSetup(clinicId);
  const { createOrder } = useClinicOrders(clinicId, false);

  const cannotCreateOrder = purchasableItems.length === 0;

  return (
    <>
      <Seo title="New Order" />

      {isLoading ? (
        <>
          <TrackedPageHeader
            title={intl.formatMessage(messages.newOrderTitle)}
            onNavButtonClick={goBack}
          />
          <InlineLoader />
        </>
      ) : cannotCreateOrder ? (
        <>
          <TrackedPageHeader
            title={intl.formatMessage(messages.newOrderTitle)}
            onNavButtonClick={goBack}
          />
          <TrackedEmptyState
            title="Orders not available"
            subtitle="This clinic cannot create orders at this time."
            icon={<InfoIcon />}
            trackedLabel="create-balloon-order-empty-state"
          />
        </>
      ) : (
        <OrderForm
          onSubmit={onSubmit}
          goBack={goBack}
          accountName={accountName}
          purchasableItems={purchasableItems}
          billingAddresses={billingAddresses}
          shippingAddresses={shippingAddresses}
        />
      )}
    </>
  );

  async function onSubmit(data: SapNewOrder) {
    await createOrder(data);
    toastSuccess(intl.formatMessage(messages.orderCreated));
    goBack();
  }

  function goBack() {
    toClinicOrdersPage(clinicId!);
  }
}
